import { useMemo, useState } from "react";
import {
  HotelsRawEventName,
  useEmitCoreHotelsRawEvent,
} from "@b2bportal/core-hotels";
import { LodgingMediaAssetEnum } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { HotelRoomCardDialogComponentProps } from "./HotelRoomCardDialog";

export const useHotelRoomCardDialog = ({
  room,
  nightCount,
}: HotelRoomCardDialogComponentProps) => {
  const { t } = useI18nContext();

  const { formatFiatCurrency } = useI18nContext();

  const [selectedRate, setSelectedRate] = useState(room.products[0]);

  const nightlyPrice = formatFiatCurrency(selectedRate.perNightTotal.fiat);
  const totalPrice = formatFiatCurrency(selectedRate.tripTotal.fiat);

  const detailsImage = room.roomInfo.media.find((media) => {
    return (
      media.LodgingMediaAsset === LodgingMediaAssetEnum.NonResizableImage ||
      media.LodgingMediaAsset === LodgingMediaAssetEnum.ResizableImage
    );
  });

  const roomImages = room.roomInfo.media
    .filter((media) => {
      return (
        media.LodgingMediaAsset === LodgingMediaAssetEnum.NonResizableImage ||
        media.LodgingMediaAsset === LodgingMediaAssetEnum.ResizableImage
      );
    })
    .map((media) => media.url);

  const emitOpenDetailsEvent = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelShopPageRoomsDetailsOpened
  );

  return useMemo(() => {
    return {
      context: {
        amenities: room.roomInfo.amenities,
        description: room.roomInfo.description,
        detailsImage: detailsImage,
        roomImages: roomImages,
        products: room.products,
        selectedRate,
      },
      handlers: {
        setSelectedRate,
        setSelectedRateWithRateId: (rateId: string) => {
          const product = room.products.find((p) => p.rateId?.value === rateId);
          if (product) setSelectedRate(product);
        },
        emitOpenDetailsEvent,
      },
      text: {
        roomDetailsTitle: t("core-hotels.roomSelection.roomDetailsTitle"),
        roomName: room.roomInfo.name,
        guests: room.roomInfo.maxAdults
          ? t("core-hotels.roomSelection.roomFitsGuests", {
              count: room.roomInfo.maxAdults,
            })
          : undefined,
        beds: room.roomInfo.beds.description,
        viewDetails: t("core-hotels.roomSelection.viewDetails"),
        selectedPrice: t("core-hotels.shop.price.pricePerNight", {
          price: nightlyPrice,
        }),
        totalPrice: t("core-hotels.shop.price.priceForNightsWithTaxesAndFees", {
          price: totalPrice,
          count: nightCount,
        }),
        selectRoom: t("core-hotels.shop.price.roomCTA"),
        roomDescriptionTitle: t(
          "core-hotels.roomSelection.roomDescriptionTitle"
        ),
        roomDescription: room.roomInfo.description,
        amenitiesTitle: t("core-hotels.roomSelection.amenitiesTitle"),
      },
    };
  }, [
    room,
    detailsImage,
    roomImages,
    selectedRate,
    emitOpenDetailsEvent,
    t,
    nightlyPrice,
    totalPrice,
    nightCount,
  ]);
};
